import { useState } from "react";
import Icon, { type IconName } from "./icons";
import Reveal from "./Reveal";

export type RetainerSelection = {
  label: string;
  projectType: string;
};

type RetainerSelectorProps = {
  onChoose: (selection: RetainerSelection) => void;
  onChooseService: (service: string) => void;
};

const supportTracks = [
  {
    code: "WEB.01",
    label: "Web & mobile",
    projectType: "Website or web platform",
    description: "Keep customer-facing products polished, current, and moving forward.",
    items: ["Responsive website updates", "Landing pages and CMS", "SEO enhancements", "iOS and Android support", "App deployment assistance"],
  },
  {
    code: "SYS.02",
    label: "Software & systems",
    projectType: "Custom business software",
    description: "Improve the systems your team relies on without starting a new project every time.",
    items: ["Feature enhancements", "Bug fixes", "API integrations", "Database and dashboard support", "Workflow automation"],
  },
  {
    code: "EVT.03",
    label: "Wedding technology",
    projectType: "Website or web platform",
    description: "Build and support thoughtful digital experiences for events, couples, and guests.",
    items: ["Wedding websites", "RSVP systems", "Guest management", "Vendor directories", "Event registration and apps"],
  },
  {
    code: "LRN.04",
    label: "Course platforms",
    projectType: "Custom business software",
    description: "Make online learning platforms easier to manage and better for students to use.",
    items: ["Platform maintenance", "Student portal improvements", "Learning experience optimization", "User management", "Course delivery support"],
  },
];

const team = ["Project management", "Senior engineering", "Full-stack development", "Mobile development", "UI/UX design", "QA & technical support"];

const rates = [
  {
    service: "Web development", rate: 150, icon: "web",
    description: "Thoughtful updates, fixes, and improvements for your website.",
    checkout: "https://checkout.cmjsoftware.com/b/bJeeVc99r1DZdbafJ4eAg0C",
  },
  {
    service: "Mobile development", rate: 175, icon: "mobile",
    description: "Focused development and support for your mobile experience.",
    checkout: "https://checkout.cmjsoftware.com/b/3cI3cu4TbgyTfjigN8eAg0D",
  },
  {
    service: "Software engineering", rate: 200, icon: "automation",
    description: "Features, integrations, and fixes for the systems you rely on.",
    checkout: "https://checkout.cmjsoftware.com/b/00w8wOclD5UfdbafJ4eAg0E",
  },
  {
    service: "Technical consulting", rate: 250, icon: "strategy",
    description: "Expert guidance for your next technical decision or challenge.",
    checkout: "https://checkout.cmjsoftware.com/b/aFa4gy1GZgyTdbaeF0eAg0F",
  },
] satisfies { service: string; rate: number; icon: IconName; description: string; checkout: string }[];

const RetainerSelector = ({ onChoose, onChooseService }: RetainerSelectorProps) => {
  const [active, setActive] = useState(0);
  const selection = supportTracks[active];

  return (
    <section className="retainer-section" id="retainer">
      <div className="site-container">
        <Reveal className="retainer-heading">
          <div>
            <p className="section-kicker">Simple ongoing support</p>
            <h2>One retainer.<br/><span>Six specialist roles.</span></h2>
          </div>
          <p>Reserve flexible capacity for improvements, support, and technical guidance—then use it where it creates the most value.</p>
        </Reveal>

        <Reveal delay={90}>
          <div className="retainer-console">
            <div className="retainer-console-head">
              <span>CMJ / FLEXIBLE CAPACITY</span>
              <span className="retainer-live"><i /> Available</span>
            </div>

            <div className="retainer-metrics" aria-label="Retainer summary">
              <div><small>Retainer hours</small><strong>20</strong><span>focused hours</span></div>
              <div><small>Usage window</small><strong>1–3</strong><span>months</span></div>
              <div className="retainer-investment"><small>Investment</small><strong>$3,500</strong><span>total</span></div>
            </div>

            <div className="retainer-body">
              <div className="retainer-track-picker">
                <p>Choose your primary focus</p>
                <div role="tablist" aria-label="Retainer support focus">
                  {supportTracks.map((track, index) => (
                    <button
                      key={track.code}
                      type="button"
                      role="tab"
                      aria-selected={active === index}
                      className={active === index ? "is-active" : ""}
                      onClick={() => setActive(index)}
                    >
                      <span>{track.code}</span>
                      <strong>{track.label}</strong>
                      <i />
                    </button>
                  ))}
                </div>
              </div>

              <div className="retainer-track-detail" role="tabpanel" key={selection.code}>
                <span>{selection.code} / SELECTED FOCUS</span>
                <h3>{selection.label}</h3>
                <p>{selection.description}</p>
                <ul>
                  {selection.items.map((item) => <li key={item}><Icon name="check" />{item}</li>)}
                </ul>
              </div>

              <aside className="retainer-choice">
                <span>Your selection</span>
                <h3>20-hour retainer</h3>
                <p>{selection.label}</p>
                <dl>
                  <div><dt>Critical response target</dt><dd>Within 4 business hours</dd></div>
                  <div><dt>Standard response target</dt><dd>Within 1 business day</dd></div>
                </dl>
                <button className="button" type="button" onClick={() => onChoose({ label: selection.label, projectType: selection.projectType })}>
                  Choose this retainer <Icon name="arrow" />
                </button>
                <small>Final scope and timing are confirmed in writing before work begins.</small>
              </aside>
            </div>
          </div>
        </Reveal>

        <div className="retainer-transparency">
          <Reveal className="retainer-team">
            <p className="section-kicker">Specialists available</p>
            <div>{team.map((role) => <span key={role}><i />{role}</span>)}</div>
          </Reveal>
          <Reveal className="retainer-rates" delay={80}>
            <div className="retainer-rates-head"><p className="section-kicker">Additional authorized hours</p><span>Only added with written approval</span></div>
            <dl>{rates.map(({ service, rate }) => <div key={service}><dt>{service}</dt><dd>${rate}/hr</dd></div>)}</dl>
          </Reveal>
        </div>

        <section className="a-la-carte" id="a-la-carte" aria-labelledby="a-la-carte-title">
          <Reveal className="a-la-carte-heading">
            <div>
              <p className="section-kicker">À la carte services</p>
              <h2 id="a-la-carte-title">Just the help you need.</h2>
            </div>
            <span className="a-la-carte-badge">No retainer required</span>
          </Reveal>
          <Reveal>
            <p className="a-la-carte-intro">Have a specific task in mind? Choose focused, hourly support without committing to the 20-hour retainer. Confirm your scope and hours with us in writing, then pay for your approved service below.</p>
          </Reveal>
          <div className="a-la-carte-grid">
            {rates.map(({ service, rate, icon, description, checkout }, index) => (
              <Reveal key={service} delay={index * 60}>
                <article className="a-la-carte-card">
                  <div className="a-la-carte-icon"><Icon name={icon} /></div>
                  <h3>{service}</h3>
                  <p>{description}</p>
                  <div className="a-la-carte-price"><strong>${rate}</strong><span>/hr</span></div>
                  <button className="button" type="button" onClick={() => onChooseService(service)}>Request this service <Icon name="arrow" /></button>
                  <a className="button button-secondary" href={checkout} target="_blank" rel="noopener noreferrer" aria-label={`Pay for ${service.toLowerCase()} at $${rate} per hour (opens in a new tab)`}>Pay for service <Icon name="arrow" /></a>
                </article>
              </Reveal>
            ))}
          </div>
          <p className="a-la-carte-note"><Icon name="check" /><span>Additional authorized hours are only added with written approval. <a href="#contact">Need to confirm your scope? Let’s talk.</a></span></p>
        </section>

        <p className="retainer-exclusions">Third-party costs—including hosting, domains, cloud infrastructure, API subscriptions, app-store fees, licensing, stock media, outsourced services, and rush work—are separate unless included in writing.</p>
      </div>
    </section>
  );
};

export default RetainerSelector;
